/**
 * Verser les pièces d'un palier de gamme dans les articles d'un bon (2026-10-18).
 *
 * Demande de Benjamin : « palier réalisé + articles : je veux choisir une gamme, rajouter ou enlever une pièce. On garde
 * la référence d'origine, sauf si elle n'est plus au tarif ; on la remplace par la nouvelle. »
 *
 * Ce que le bouton verse : les lignes de REMPLACEMENT du palier (jamais les contrôles, jamais les « si nécessaire »), avec
 * la référence, la désignation, la quantité de la gamme (vide quand elle est inconnue : le bon la demandera, jamais « 1 ») et
 * son unité. AUCUN prix : un bon n'en porte pas (ADR-0011). Chaque ligne versée reste une ligne ordinaire : modifiable,
 * retirable ; ce qui n'a pas été consommé s'enlève, ce qui manque s'ajoute à la main.
 *
 * La gamme elle-même ne change jamais : seul le bon est touché.
 *
 * Verser un autre palier REMPLACE ce que le bouton avait versé (2026-10-19) : les paliers sont cumulatifs, les ajouter par-dessus
 * mettrait les pièces en double. Chaque ligne versée porte son origine (la gamme et le palier), ENREGISTRÉE avec le brouillon ;
 * une ligne saisie à la main n'en a pas et n'est jamais touchée.
 *
 * Le contrôle au tarif (`verifierReferencesBon`) décide pour chaque référence : au tarif, elle reste ; disparue, elle est
 * remplacée toute seule QUAND c'est sûr (une seule référence du tarif commence pareil avec la même désignation), sinon la
 * ligne est signalée avec ses candidats. Ce qui est dit sur chaque ligne (« remplacée… », « à choisir… ») n'est qu'un
 * constat de l'écran, jamais enregistré : il disparaît dès que la référence de la ligne change.
 *
 * Module pur : aucun appel réseau.
 */
import type {
  CandidatReferenceBon,
  GammeContenu,
  GammeDetail,
  GammeLigne,
  GammeNiveau,
  ReferencesVerifieesBon,
} from '../api/client';
import { nouvelleCle } from './gammes-edition';
import { ligneBonVide, type BonEditable, type LigneBonEditable } from './bon-edition';
import { decrireNiveau } from './gammes';

// ------------------------------------------------------------------- les pièces d'un palier

/** Une quantité de gamme en texte de saisie : vide quand elle est inconnue (jamais « 1 »), la virgule française sinon. */
export function quantiteEnTexte(quantite: number | null): string {
  return quantite === null ? '' : String(quantite).replace('.', ',');
}

/** Les lignes d'un palier que le bouton verse : seulement les REMPLACEMENTS, ni les contrôles ni les « si nécessaire ». */
export function piecesDuPalier(niveau: GammeNiveau): GammeLigne[] {
  return niveau.lines.filter((l) => l.kind === 'REPLACE');
}

export interface PalierDeGamme {
  readonly levelKey: string;
  readonly code: string;
  /** « Palier B — tous les 12 000 h à partir de 6 000 h ». */
  readonly libelle: string;
  readonly rank: number;
  /** Combien de pièces le bouton versera. */
  readonly pieces: number;
}

/** Les paliers d'une gamme, du moins englobant au plus, avec le nombre de pièces que le bouton verserait. */
export function paliersDeGamme(contenu: GammeContenu | null | undefined): PalierDeGamme[] {
  return [...(contenu?.levels ?? [])]
    .sort((a, b) => a.rank - b.rank)
    .map((n) => ({
      levelKey: n.levelKey,
      code: n.code,
      libelle: `Palier ${n.code} — ${n.label !== '' ? n.label : decrireNiveau(n)}`,
      rank: n.rank,
      pieces: piecesDuPalier(n).length,
    }));
}

/** Le texte du bouton : « Ajouter les 9 pièces du palier B » ; au singulier « Ajouter la pièce du palier A ». */
export function libelleDuBouton(palier: Pick<PalierDeGamme, 'code' | 'pieces'> | null): string {
  if (!palier) return 'Ajouter les pièces du palier';
  if (palier.pieces === 0) return `Aucune pièce à remplacer au palier ${palier.code}`;
  if (palier.pieces === 1) return `Ajouter la pièce du palier ${palier.code}`;
  return `Ajouter les ${palier.pieces} pièces du palier ${palier.code}`;
}

/** La version EN VIGUEUR d'une gamme lue en détail : celle que la liste annonce, publiée et non archivée ; `null` sans version. */
export function contenuEnVigueur(detail: Pick<GammeDetail, 'enVigueur' | 'versions'>): GammeContenu | null {
  if (!detail.enVigueur) return null;
  const numero = detail.enVigueur.versionNumber;
  const version = detail.versions.find((v) => v.versionNumber === numero && v.statut === 'PUBLIEE' && v.archivedAt === null);
  return version?.contenu ?? null;
}

// ------------------------------------------------------------------- d'où vient une ligne

/**
 * Les lignes du bon que le bouton a versées : celles qui portent une origine (la gamme et le palier, enregistrées avec le
 * brouillon). Une ligne saisie à la main n'en a pas : le bouton n'y touche jamais.
 */
export function lignesVerseesParLeBouton(bon: BonEditable): LigneBonEditable[] {
  return bon.lignes.filter((l) => l.versee !== undefined);
}

// ------------------------------------------------------------------- verser

/** Une ligne d'article (pièce) pour chaque pièce du palier, avec une clé neuve et son origine (la gamme et le palier). */
export function lignesDuPalier(niveau: GammeNiveau, gammeId: string): LigneBonEditable[] {
  return piecesDuPalier(niveau).map((p) => ({
    ...ligneBonVide('PART'),
    lineKey: nouvelleCle(),
    reference: (p.reference ?? '').trim(),
    designation: p.designation,
    quantite: quantiteEnTexte(p.quantity),
    unite: p.unit ?? '',
    versee: { gammeId, palier: niveau.code },
  }));
}

/**
 * Verse les lignes d'un palier dans le bon en REMPLAÇANT ce que le bouton avait déjà versé (Benjamin, 2026-10-19 : « si je me
 * trompe, je mets la 3000 h au lieu de la 6000 h, tu doubles les pièces »). Les paliers sont cumulatifs : le 6 000 h contient déjà
 * toutes les pièces du 3 000 h, les ajouter par-dessus les mettrait en double.
 *
 * - les lignes versées avant sont retirées, les nouvelles prennent la place de la première (le bloc reste là où il était) ;
 * - les lignes saisies à la main (sans origine) ne bougent jamais, ni de place ni de contenu ;
 * - sans rien de versé avant, les lignes s'ajoutent à la fin.
 */
export function verserLesLignes(bon: BonEditable, lignes: readonly LigneBonEditable[]): BonEditable {
  const premiere = bon.lignes.findIndex((l) => l.versee !== undefined);
  if (premiere === -1) return { ...bon, lignes: [...bon.lignes, ...lignes] };
  const gardees = bon.lignes.filter((l) => l.versee === undefined);
  // Avant la première ligne versée, il n'y a que des lignes saisies à la main : `premiere` est donc aussi leur nombre.
  return { ...bon, lignes: [...gardees.slice(0, premiere), ...lignes, ...gardees.slice(premiere)] };
}

function listeFr(mots: readonly string[]): string {
  return mots.length <= 1 ? mots.join('') : `${mots.slice(0, -1).join(', ')} et ${mots[mots.length - 1]}`;
}

/**
 * Ce que l'on demande avant de remplacer : « Les 5 pièces déjà versées du palier A seront remplacées par les 9 pièces du palier
 * B. Les 2 lignes saisies à la main restent. » `null` quand le bouton n'a encore rien versé (rien à remplacer, aucune question).
 * Les modifications faites sur une ligne versée (une quantité changée, par exemple) disparaissent avec elle : c'est la règle,
 * une ligne versée est une ligne du palier tant qu'elle n'a pas été retirée.
 */
export function confirmationDuRemplacement(bon: BonEditable, nouveau: { readonly code: string; readonly pieces: number }): string | null {
  const versees = lignesVerseesParLeBouton(bon);
  const n = versees.length;
  if (n === 0) return null;
  const saisies = bon.lignes.length - n;
  const codes = [...new Set(versees.map((l) => l.versee?.palier ?? ''))].sort((a, b) => a.localeCompare(b, 'fr'));
  const duPalier = codes.length === 1 ? `du palier ${codes[0]}` : `des paliers ${listeFr(codes)}`;
  const sujet = n === 1 ? `La pièce déjà versée ${duPalier} sera remplacée` : `Les ${n} pièces déjà versées ${duPalier} seront remplacées`;
  const par = nouveau.pieces === 1 ? `la pièce du palier ${nouveau.code}` : `les ${nouveau.pieces} pièces du palier ${nouveau.code}`;
  const reste = saisies === 0 ? '' : saisies === 1 ? ' La ligne saisie à la main reste.' : ` Les ${saisies} lignes saisies à la main restent.`;
  return `${sujet} par ${par}.${reste}`;
}

// ------------------------------------------------------------------- le contrôle au tarif

/** Ce que l'écran dit d'une ligne après le contrôle au tarif. */
export type VerdictLigne =
  /** Remplacée toute seule : l'ancienne n'est plus au tarif, la nouvelle est la seule à commencer pareil avec la même désignation. */
  | { readonly type: 'REMPLACEE'; readonly ancienne: string; readonly nouvelle: string }
  /** Plus au tarif, remplacement pas sûr : la ligne garde sa référence d'origine, les candidats se choisissent. */
  | { readonly type: 'A_CHOISIR'; readonly reference: string; readonly candidats: readonly CandidatReferenceBon[] }
  /** L'utilisateur a choisi un candidat à la place de la référence d'origine. */
  | { readonly type: 'CHOISIE'; readonly ancienne: string; readonly nouvelle: string };

export type Verdicts = Readonly<Record<string, VerdictLigne>>;

/**
 * Ce que l'on montre pour une ligne : le verdict, SEULEMENT s'il parle encore de la référence qui est dans la case.
 * Dès que l'utilisateur change la référence à la main, le constat n'a plus d'objet : il disparaît tout seul.
 */
export function verdictAffiche(ligne: Pick<LigneBonEditable, 'reference'>, verdict: VerdictLigne | undefined): VerdictLigne | null {
  if (!verdict) return null;
  const courante = ligne.reference.trim();
  if (verdict.type === 'A_CHOISIR') return courante === verdict.reference ? verdict : null;
  return courante === verdict.nouvelle ? verdict : null;
}

export interface BilanControle {
  readonly bon: BonEditable;
  readonly verdicts: Verdicts;
  readonly remplacees: number;
  readonly aChoisir: number;
  /** Le fabricant n'a aucun tarif en vigueur : rien n'a pu être contrôlé. */
  readonly tarifNonCharge: boolean;
}

/**
 * Applique le résultat du contrôle aux lignes qui viennent d'être versées. `envoyees` = les lignes contrôlées, dans l'ordre
 * de la demande, avec la référence qui a été envoyée. Une ligne que l'utilisateur a modifiée ENTRE la demande et la réponse
 * (sa référence n'est plus celle qui a été contrôlée, ou elle a été retirée) n'est jamais touchée.
 */
export function appliquerLeControle(
  bon: BonEditable,
  envoyees: readonly { readonly lineKey: string; readonly reference: string }[],
  resultat: ReferencesVerifieesBon,
  verdictsActuels: Verdicts = {},
): BilanControle {
  let courant = bon;
  const verdicts: Record<string, VerdictLigne> = { ...verdictsActuels };
  let remplacees = 0;
  let aChoisir = 0;
  let tarifNonCharge = false;

  envoyees.forEach((e, i) => {
    const verifiee = resultat.items[i];
    if (!verifiee) return;
    if (verifiee.etat === 'TARIF_NON_CHARGE') {
      tarifNonCharge = true;
      return;
    }
    const index = courant.lignes.findIndex((l) => l.lineKey === e.lineKey);
    const ligne = courant.lignes[index];
    if (!ligne || ligne.reference.trim() !== e.reference) return;

    if (verifiee.etat === 'REMPLACEE' && verifiee.remplacement) {
      courant = majLigne(courant, index, { reference: verifiee.remplacement.reference, designation: verifiee.remplacement.description });
      verdicts[e.lineKey] = { type: 'REMPLACEE', ancienne: e.reference, nouvelle: verifiee.remplacement.reference };
      remplacees += 1;
    } else if (verifiee.etat === 'A_CHOISIR') {
      verdicts[e.lineKey] = { type: 'A_CHOISIR', reference: e.reference, candidats: verifiee.candidats };
      aChoisir += 1;
    }
  });
  return { bon: courant, verdicts, remplacees, aChoisir, tarifNonCharge };
}

/**
 * L'utilisateur choisit un candidat pour une ligne « à choisir » : la référence ET la désignation sont celles du tarif (une
 * autre référence peut être une autre pièce : garder l'ancien texte ferait mentir la ligne).
 */
export function choisirUnCandidat(
  bon: BonEditable,
  lineKey: string,
  candidat: CandidatReferenceBon,
  verdictsActuels: Verdicts,
): { readonly bon: BonEditable; readonly verdicts: Verdicts } {
  const index = bon.lignes.findIndex((l) => l.lineKey === lineKey);
  const ligne = bon.lignes[index];
  if (!ligne) return { bon, verdicts: verdictsActuels };
  return {
    bon: majLigne(bon, index, { reference: candidat.reference, designation: candidat.description }),
    verdicts: { ...verdictsActuels, [lineKey]: { type: 'CHOISIE', ancienne: ligne.reference.trim(), nouvelle: candidat.reference } },
  };
}

function majLigne(bon: BonEditable, index: number, changement: Partial<Omit<LigneBonEditable, 'lineKey'>>): BonEditable {
  return { ...bon, lignes: bon.lignes.map((l, i) => (i === index ? { ...l, ...changement } : l)) };
}

// ------------------------------------------------------------------- les phrases

/** Ce que l'écran dit d'une ligne contrôlée (la ligne verte ou orange sous sa désignation). */
export function phraseDuVerdict(verdict: VerdictLigne): string {
  switch (verdict.type) {
    case 'REMPLACEE':
      return `Remplacée par la nouvelle : l’ancienne ${verdict.ancienne} n’est plus au tarif ; c’est la seule référence du tarif qui commence pareil avec la même désignation.`;
    case 'CHOISIE':
      return `Remplacée par la référence choisie : l’ancienne ${verdict.ancienne} n’est plus au tarif.`;
    case 'A_CHOISIR':
      return verdict.candidats.length > 0
        ? `La référence ${verdict.reference} n’est plus au tarif et le remplacement n’est pas sûr : choisissez la nouvelle ci-dessous, ou tapez son début dans la case.`
        : `La référence ${verdict.reference} n’est plus au tarif et rien ne commence pareil : tapez le début de la nouvelle référence dans la case, la liste du tarif vous la propose.`;
  }
}

/** « 9 pièces ajoutées (palier B), à la place des 5 pièces versées avant. » — la seconde partie seulement quand le bouton avait déjà versé. */
export function phraseDesPiecesAjoutees(nombre: number, palier: string, retirees = 0): string {
  const aLaPlace = retirees === 0 ? '' : retirees === 1 ? ', à la place de la pièce versée avant' : `, à la place des ${retirees} pièces versées avant`;
  return `${nombre} ${nombre > 1 ? 'pièces ajoutées' : 'pièce ajoutée'} (palier ${palier})${aLaPlace}.`;
}

/** Le compte rendu après avoir versé les pièces et contrôlé leurs références. */
export function phraseDuVersement(
  nombre: number,
  palier: string,
  bilan: Pick<BilanControle, 'remplacees' | 'aChoisir' | 'tarifNonCharge'> | null,
  retirees = 0,
): string {
  const base = phraseDesPiecesAjoutees(nombre, palier, retirees);
  if (!bilan) return `${base} Leurs références n’ont pas pu être contrôlées au tarif : vérifiez-les.`;
  const morceaux: string[] = [];
  if (bilan.remplacees > 0) morceaux.push(`${bilan.remplacees} ${bilan.remplacees > 1 ? 'références remplacées' : 'référence remplacée'} par celle du tarif`);
  if (bilan.aChoisir > 0) morceaux.push(`${bilan.aChoisir} à choisir (plus au tarif)`);
  const controle = morceaux.length > 0 ? ` ${morceaux.join(', ')}.` : bilan.tarifNonCharge ? '' : ' Toutes les références sont au tarif.';
  const nonCharge = bilan.tarifNonCharge ? ' Le tarif du constructeur n’est pas chargé : les références ne sont pas contrôlées.' : '';
  return `${base}${controle}${nonCharge}`;
}
